import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { RealtimeService } from 'src/app/services/realtime.service';
import { AiPipeline, AiRun, AiRunRealtimeEvent } from '../../core/models/ai.model';
import { AiPipelineService, AiRunService } from '../../core/services/ai-api.service';
import { AI_MODEL_RUN_EXECUTOR, AiPipelineRunExecutor, AiRunExecutor } from './ai-runner-executor';
import { AiRunnerInput, AiRunnerTarget } from './ai-runner.model';

/**
 * Tudo que o runner precisa do mundo externo: catálogo de pipelines, início/consulta/cancelamento
 * da execução e o fluxo de eventos em tempo real. O padrão fala com a API de IA do v3ndor; para
 * outro backend (ou um fake em testes) basta prover `AI_RUNNER_BACKEND`.
 */
export interface AiRunnerBackend {
  /** Lista as pipelines disponíveis; `search` filtra por nome, slug ou descrição. */
  listPipelines(search?: string): Promise<AiPipeline[]>;
  getPipeline(id: string): Promise<AiPipeline>;
  start(target: AiRunnerTarget, input: AiRunnerInput): Promise<AiRun>;
  getRun(id: string): Promise<AiRun>;
  cancelRun(id: string): Promise<unknown>;
  /** Eventos de execução (qualquer run); o runner filtra pelo id acompanhado. */
  events(): Observable<AiRunRealtimeEvent>;
  /** `false` quando o alvo (ex.: `model`) não tem executor — mostra "indisponível". */
  supports(target: AiRunnerTarget): boolean;
}

@Injectable({ providedIn: 'root' })
export class DefaultAiRunnerBackend implements AiRunnerBackend {
  private readonly pipelines = inject(AiPipelineService);
  private readonly runs = inject(AiRunService);
  private readonly realtime = inject(RealtimeService);
  private readonly pipelineExecutor = inject(AiPipelineRunExecutor);
  private readonly modelExecutor = inject(AI_MODEL_RUN_EXECUTOR, { optional: true });

  listPipelines(search?: string): Promise<AiPipeline[]> {
    return this.pipelines.options(search ? { search } : undefined);
  }

  getPipeline(id: string): Promise<AiPipeline> {
    return this.pipelines.get(id);
  }

  start(target: AiRunnerTarget, input: AiRunnerInput): Promise<AiRun> {
    const executor: AiRunExecutor | null = this.executorFor(target);
    return executor
      ? executor.start(target, input)
      : Promise.reject(new Error('Alvo não suportado.'));
  }

  getRun(id: string): Promise<AiRun> {
    return this.runs.get(id);
  }

  cancelRun(id: string): Promise<unknown> {
    return this.runs.cancel(id);
  }

  events(): Observable<AiRunRealtimeEvent> {
    return this.realtime.subscribeToAiRuns();
  }

  supports(target: AiRunnerTarget): boolean {
    return !!this.executorFor(target);
  }

  private executorFor(target: AiRunnerTarget): AiRunExecutor | null {
    return target.kind === 'pipeline' ? this.pipelineExecutor : this.modelExecutor;
  }
}

export const AI_RUNNER_BACKEND = new InjectionToken<AiRunnerBackend>('AI_RUNNER_BACKEND', {
  providedIn: 'root',
  factory: () => inject(DefaultAiRunnerBackend),
});
